import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faXmark, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { deleteAccount } from "../../../api/users";

function DeleteModal({ onClose, onConfirm, isDeleting }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) return;
    onConfirm(password);
  };

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tw-absolute tw-inset-0 tw-bg-black/60 tw-backdrop-blur-sm" />

      <div className="tw-relative tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-[#1E0039] tw-border tw-border-white/10 tw-p-6 tw-flex tw-flex-col tw-gap-5 tw-shadow-2xl">
        <div className="tw-flex tw-items-start tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500/20 tw-shrink-0">
              <FontAwesomeIcon icon={faTriangleExclamation} className="tw-text-red-400" />
            </div>
            <div>
              <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Delete Account")}</h2>
              <p className="tw-text-white/40 tw-text-xs tw-m-0 tw-mt-0.5">{t("This action is permanent and cannot be undone.")}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/5 hover:tw-bg-white/10 tw-text-white/50 hover:tw-text-white tw-transition-colors tw-border-0 tw-cursor-pointer tw-shrink-0"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <p className="tw-text-sm tw-text-white/60 tw-m-0">
          {t("Enter your password to confirm you want to permanently delete your account.")}
        </p>

        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-relative">
            <input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Your password")}
              autoFocus
              className="tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-white/20 tw-bg-white/5 tw-px-4 tw-pr-11 tw-text-sm tw-text-white placeholder:tw-text-white/30 focus:tw-border-red-500/60 focus:tw-outline-none tw-transition-colors"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/40 hover:tw-text-white/70 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer"
            >
              <FontAwesomeIcon icon={show ? faEyeSlash : faEye} className="tw-text-sm" />
            </button>
          </div>

          <div className="tw-flex tw-gap-3">
            <button
              type="button"
              onClick={onClose}
              className="tw-flex-1 tw-rounded-lg tw-border tw-border-white/20 tw-bg-transparent hover:tw-bg-white/5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white/60 hover:tw-text-white tw-transition-colors tw-cursor-pointer"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={!password || isDeleting}
              className="tw-flex-1 tw-rounded-lg tw-bg-red-600 hover:tw-bg-red-700 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-border-0 tw-cursor-pointer"
            >
              {isDeleting ? t("Deleting...") : t("Delete Account")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const token = user?.token;
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = (password) => {
    if (!user?.id) {
      toast.error(t("User session not found. Please log in again."));
      return;
    }

    setIsDeleting(true);
    deleteAccount(user.id, token, password)
      .then(() => {
        toast.success(t("Account deleted successfully"));
        dispatch({ type: "LOGOUT", payload: null });
        navigate("/");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || t("Error deleting account"));
        setIsDeleting(false);
      });
  };

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-4 tw-max-w-md">
        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/40">
            {t("Account Type")}
          </label>
          <input
            readOnly
            value={user?.role || ""}
            className="tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/5 tw-px-4 tw-text-sm tw-text-white/60 tw-outline-none tw-cursor-default"
          />
        </div>

        <div className="tw-rounded-xl tw-bg-red-900/20 tw-border tw-border-red-900/30 tw-p-5">
          <h3 className="tw-text-red-400 tw-font-semibold tw-text-sm tw-m-0 tw-mb-1">
            {t("Danger Zone")}
          </h3>
          <p className="tw-text-xs tw-text-red-400/70 tw-m-0 tw-mb-4">
            {t("Once you delete your account, there is no going back. Please be certain.")}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="tw-w-full tw-rounded-lg tw-bg-red-600 hover:tw-bg-red-700 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors tw-border-0 tw-cursor-pointer"
          >
            {t("Delete Account")}
          </button>
        </div>
      </div>

      {showModal && (
        <DeleteModal
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirm}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
